import React, { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { postsApi } from '../api/postsApi'
import type { PostDetail } from '../types'
import { SEOHead } from '../components/ui/SEOHead'
import { PostViewer } from '../components/blog/PostViewer'
import { LoadingSpinner } from '../components/ui/LoadingSpinner'
import { TerminalWindow } from '../components/terminal/TerminalWindow'
import { CommentSection } from '../components/comments/CommentSection'

// ── Geo resolution ─────────────────────────────────────────────────────────────
// ipapi.co returns country/city/timezone from the caller's IP.
// We forward ONLY the geo fields to our backend — the IP address is never sent
// or stored by us. Falls back gracefully if the lookup fails or is offline.
async function resolveGeo() {
  try {
    const res = await fetch('https://ipapi.co/json/', { signal: AbortSignal.timeout(4000) })
    if (!res.ok) throw new Error('geo fetch failed')
    const d = await res.json()
    return {
      countryCode: d.country         ?? null,
      country:     d.country_name    ?? null,
      region:      d.region          ?? null,
      city:        d.city            ?? null,
      timezone:    d.timezone        ?? null,
      language:    navigator.language ?? null,
    }
  } catch {
    return {
      countryCode: null,
      country:     null,
      region:      null,
      city:        null,
      timezone:    Intl.DateTimeFormat().resolvedOptions().timeZone ?? null,
      language:    navigator.language ?? null,
    }
  }
}

export const PostPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>()
  const [post, setPost] = useState<PostDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!slug) return
    setLoading(true)
    setNotFound(false)
    setError(null)

    postsApi
      .getBySlug(slug)
      .then((data) => {
        if (data === null) {
          setNotFound(true)
        } else {
          setPost(data)
        }
        setLoading(false)
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Failed to load post')
        setLoading(false)
      })
  }, [slug])

  // Record a unique view once per browser session per post
  useEffect(() => {
    if (!post?.slug) return
    const key = `viewed:${post.slug}`
    if (sessionStorage.getItem(key)) return
    sessionStorage.setItem(key, '1')
    resolveGeo().then((geo) => postsApi.recordView(post.slug, geo))
  }, [post?.slug])

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '80px 0',
          gap: '12px',
          fontFamily: "'JetBrains Mono', 'Courier New', monospace",
          color: 'var(--text-secondary)',
          fontSize: '0.85rem',
        }}
      >
        <LoadingSpinner />
        <span>loading post...</span>
      </div>
    )
  }

  if (notFound) {
    return (
      <>
        <SEOHead title="Post Not Found" />
        <TerminalWindow title="404.sh">
          <div
            style={{
              fontFamily: "'JetBrains Mono', 'Courier New', monospace",
              fontSize: '0.9rem',
            }}
          >
            <div style={{ color: 'var(--accent-red, #ff4444)', marginBottom: '12px' }}>
              Error 404: Post not found — &quot;{slug}&quot;
            </div>
            <div style={{ color: 'var(--text-secondary)', marginBottom: '20px' }}>
              The post you are looking for does not exist or has been removed.
            </div>
            <Link
              to="/"
              style={{ color: 'var(--accent-cyan)', fontSize: '0.85rem' }}
            >
              [&lt; back to home]
            </Link>
          </div>
        </TerminalWindow>
      </>
    )
  }

  if (error) {
    return (
      <div
        style={{
          padding: '32px',
          fontFamily: "'JetBrains Mono', 'Courier New', monospace",
          color: 'var(--accent-red, #ff4444)',
          fontSize: '0.9rem',
        }}
      >
        [ERROR] {error}
      </div>
    )
  }

  if (!post) return null

  return (
    <>
      <SEOHead
        title={post.title}
        description={post.summary}
        image={post.coverImageUrl}
        url={`/posts/${post.slug}`}
        type="article"
        publishedAt={post.publishedAt}
        updatedAt={post.updatedAt}
        author={post.author}
        tags={post.tags}
      />
      <PostViewer post={post} />
      <CommentSection slug={post.slug} />
    </>
  )
}
